import React, { useState, useEffect } from 'react';
import { Play, Eye, Calendar, Volume2, VolumeX, X, ChevronRight, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FALLBACK_GALLERY = [
    { id: '1', type: 'youtube', url: 'kYR5Z-hWcIE', title: 'Concierto Madrid 2025', filter: 'youtube', date: '2025-01-20', views: 154000 },
    { id: '2', type: 'tiktok', url: 'https://www.tiktok.com/@mabelita_dc/video/7461947231454645526', title: 'Viral Tunantada', filter: 'tiktok', date: '2025-01-10', views: 852000 },
    { id: '3', type: 'image', url: '/assets/images/hero_mabelita.jpg', title: 'Show en Vivo', filter: 'images', date: '2024-12-15', views: 12000 },
    { id: '4', type: 'youtube', url: 'dQw4w9WgXcQ', title: 'Mix Huaylash', filter: 'youtube', date: '2024-11-05', views: 520000 },
    { id: '5', type: 'tiktok', url: 'https://www.tiktok.com/@mabelita_dc/video/7476722838386150678', title: 'Detrás de Escena', filter: 'tiktok', date: '2025-02-01', views: 340000 },
    { id: '6', type: 'image', url: '/assets/images/weaver_mabelita.jpg', title: 'Artesanía Fina', filter: 'images', date: '2024-10-22', views: 8000 },
    { id: '7', type: 'youtube', url: 'jNQXAC9IVRw', title: 'Presentación TV', filter: 'youtube', date: '2024-09-14', views: 210000 },
    { id: '8', type: 'tiktok', url: 'https://www.tiktok.com/@mabelita_dc/video/7461947231454645526', title: 'Trend Andino', filter: 'tiktok', date: '2025-01-25', views: 1200000 },
];

export const InteractiveGallery = ({ layout, siteConfig }: { layout: string, siteConfig?: any }) => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [activeSort, setActiveSort] = useState('recent'); // 'recent' or 'views'
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [isMuted, setIsMuted] = useState(false);

    // Auto-scroll al reproductor cuando se abre
    useEffect(() => {
        if (selectedItem) {
            setTimeout(() => {
                const playerEl = document.getElementById('interactive-gallery-player');
                if (playerEl) {
                    const y = playerEl.getBoundingClientRect().top + window.scrollY - 100; // Offset para el navbar
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }, 100);
        }
    }, [selectedItem]);

    const filters = [
        { id: 'all', label: 'Todos' },
        { id: 'youtube', label: 'YouTube' },
        { id: 'tiktok', label: 'TikTok' },
        { id: 'images', label: 'Fotos' }
    ];

    const sorts = [
        { id: 'recent', label: 'Más Recientes' },
        { id: 'views', label: 'Más Vistos' }
    ];

    const galleryItems = (siteConfig?.gallery_items && Array.isArray(siteConfig.gallery_items) && siteConfig.gallery_items.length > 0)
        ? siteConfig.gallery_items
        : FALLBACK_GALLERY;

    let filteredItems = activeFilter === 'all' ? [...galleryItems] : galleryItems.filter((i: any) => i.filter === activeFilter);

    filteredItems.sort((a: any, b: any) => {
        if (activeSort === 'views') return b.views - a.views;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    const getThumbnail = (item: any) => {
        if (item.type === 'image') return item.url;
        if (item.type === 'youtube') return `https://img.youtube.com/vi/${item.url}/hqdefault.jpg`;
        // Fallback for tiktok until custom thumbs are generated
        return '/assets/images/singer_mabelita.jpg';
    };

    return (
        <div className="w-full">
            {/* Controls Container */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-start md:items-center bg-white/5 p-4 rounded-2xl border border-white/10 backdrop-blur-sm">

                {/* Filters */}
                <div className="flex flex-wrap gap-2">
                    {filters.map(f => (
                        <button
                            key={f.id}
                            onClick={() => setActiveFilter(f.id)}
                            className={`px-4 py-1.5 rounded-full font-bold text-xs tracking-widest uppercase transition-all ${activeFilter === f.id
                                ? (layout === 'urban' ? 'bg-mabelita-accent text-black' : 'bg-mabelita-primary text-white shadow-md')
                                : (layout === 'editorial' ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white')
                                }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Sorts */}
                <div className="flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-600/30 pt-3 md:pt-0 md:pl-4 w-full md:w-auto">
                    {sorts.map(s => (
                        <button
                            key={s.id}
                            onClick={() => setActiveSort(s.id)}
                            className={`px-4 py-1.5 rounded-full font-bold text-xs tracking-widest uppercase transition-all flex items-center gap-1 ${activeSort === s.id
                                ? (layout === 'editorial' ? 'text-black border border-black bg-gray-100' : 'text-white border border-white/30 bg-white/10')
                                : 'text-gray-500 border border-transparent hover:text-gray-400'
                                }`}
                        >
                            {s.id === 'recent' ? <Calendar size={12} /> : <Eye size={12} />}
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Inline Minimalist Media-Focused Player */}
            <AnimatePresence mode="wait">
                {selectedItem && (() => {
                    const currentIndex = filteredItems.findIndex((i: any) => i.id === selectedItem.id);
                    const hasNext = currentIndex !== -1 && currentIndex < filteredItems.length - 1;
                    const hasPrev = currentIndex > 0;

                    const goNext = (e: any) => { e.stopPropagation(); setSelectedItem(filteredItems[currentIndex + 1]); setIsMuted(false); };
                    const goPrev = (e: any) => { e.stopPropagation(); setSelectedItem(filteredItems[currentIndex - 1]); setIsMuted(false); };

                    // Extract TikTok ID if needed
                    let tiktokId = '';
                    if (selectedItem.type === 'tiktok') {
                        const match = selectedItem.url.match(/video\/(\d+)/);
                        if (match) tiktokId = match[1];
                    }

                    return (
                        <motion.div
                            id="interactive-gallery-player"
                            key="inline-player"
                            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                            animate={{ opacity: 1, height: 'auto', marginBottom: 32 }}
                            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                            transition={{ duration: 0.4, ease: 'easeInOut' }}
                            className="w-full relative rounded-2xl md:rounded-3xl overflow-hidden bg-black shadow-2xl border border-white/10"
                        >
                            {/* Top Bar: Clickable Title & Close */}
                            <div className="absolute top-0 inset-x-0 p-3 md:p-4 flex items-center justify-between z-[120] bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
                                <a
                                    href={selectedItem.type !== 'image' ? (selectedItem.type === 'youtube' ? `https://youtube.com/watch?v=${selectedItem.url}` : selectedItem.url) : '#'}
                                    target={selectedItem.type !== 'image' ? "_blank" : "_self"}
                                    rel="noreferrer"
                                    className="text-white font-bold text-base md:text-xl hover:text-[#CCFF00] transition-colors truncate max-w-[80vw] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] px-2 pointer-events-auto"
                                    onClick={e => { if (selectedItem.type === 'image') e.preventDefault(); }}
                                >
                                    {selectedItem.title}
                                </a>
                                <button
                                    onClick={() => { setSelectedItem(null); setIsMuted(false); }}
                                    className="text-white hover:text-red-400 transition-colors bg-white/10 hover:bg-white/20 p-2 rounded-full backdrop-blur-md pointer-events-auto"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Center Media Container */}
                            <div className="relative w-full flex items-center justify-center p-0 pt-12 pb-2 md:p-2 md:pt-14 md:pb-4 bg-black">

                                {/* Navigation Arrows */}
                                {hasPrev && (
                                    <button
                                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-[110] text-white/50 hover:text-white hover:scale-110 bg-black/40 hover:bg-black/70 rounded-full transition-all p-1 md:p-2 backdrop-blur-sm pointer-events-auto"
                                        onClick={goPrev}
                                    >
                                        <ChevronLeft strokeWidth={1.5} className="w-8 h-8 md:w-10 md:h-10" />
                                    </button>
                                )}
                                {hasNext && (
                                    <button
                                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-[110] text-white/50 hover:text-white hover:scale-110 bg-black/40 hover:bg-black/70 rounded-full transition-all p-1 md:p-2 backdrop-blur-sm pointer-events-auto"
                                        onClick={goNext}
                                    >
                                        <ChevronRight strokeWidth={1.5} className="w-8 h-8 md:w-10 md:h-10" />
                                    </button>
                                )}

                                <AnimatePresence mode="popLayout">
                                    <motion.div
                                        key={selectedItem.id}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3 }}
                                        className="relative w-full h-full flex items-center justify-center pointer-events-auto"
                                    >
                                        {/* Image Viewer */}
                                        {selectedItem.type === 'image' && (
                                            <img src={selectedItem.url} className="w-auto max-w-full max-h-[75vh] object-contain drop-shadow-2xl rounded-xl" alt={selectedItem.title} />
                                        )}

                                        {/* YouTube Viewer */}
                                        {selectedItem.type === 'youtube' && (
                                            <div className="relative w-full max-w-[100vw] lg:max-w-6xl aspect-video bg-black rounded-lg md:rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                                                <iframe
                                                    className="w-full h-full"
                                                    src={`https://www.youtube.com/embed/${selectedItem.url}?autoplay=1&mute=${isMuted ? 1 : 0}&modestbranding=1&controls=1&rel=0`}
                                                    title={selectedItem.title}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                ></iframe>
                                                {/* Audio Toggle Overlay */}
                                                <div className="absolute bottom-4 left-4 md:bottom-auto md:left-auto md:top-4 md:right-4 z-[130]">
                                                    <button
                                                        onClick={(e) => { e.preventDefault(); setIsMuted(!isMuted); }}
                                                        className="bg-black/70 hover:bg-mabelita-primary text-white p-3 rounded-full backdrop-blur-md transition-colors shadow-lg flex items-center justify-center outline-none ring-2 ring-white/20"
                                                        title={isMuted ? "Activar Sonido" : "Silenciar"}
                                                    >
                                                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* TikTok Viewer */}
                                        {selectedItem.type === 'tiktok' && tiktokId && (
                                            <div className="relative w-full max-w-[320px] md:max-w-[400px] aspect-[9/16] bg-black rounded-lg md:rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] flex items-center justify-center">
                                                <iframe
                                                    className="w-full h-full absolute inset-0 md:rounded-2xl"
                                                    src={`https://www.tiktok.com/embed/v2/${tiktokId}?lang=es-ES`}
                                                    title={selectedItem.title}
                                                    frameBorder="0"
                                                    allow="autoplay; encrypted-media;"
                                                    allowFullScreen
                                                ></iframe>
                                            </div>
                                        )}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    );
                })()}
            </AnimatePresence>

            {/* Masonry Layout for Thumbnails */}
            <div className={`columns-2 sm:columns-3 md:columns-4 lg:columns-4 gap-4 space-y-4 ${filteredItems.length === 0 ? 'min-h-[200px]' : ''}`}>
                {filteredItems.map((item: any, idx: number) => (
                    <div
                        key={`${item.id}-${idx}`}
                        onClick={() => setSelectedItem(item)}
                        className={`break-inside-avoid group relative bg-gray-900 rounded-xl overflow-hidden cursor-pointer shadow-lg animate-in fade-in zoom-in duration-300 border border-white/5 hover:border-white/20 transition-all ${selectedItem?.id === item.id ? 'ring-4 ring-mabelita-primary scale-[1.02]' : ''}`}
                    >
                        {/* Thumbnail properly adjusts its height */}
                        <img src={getThumbnail(item)} alt={item.title} className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100 block" />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end p-3">
                            {item.type !== 'image' && (
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white group-hover:scale-110 group-hover:bg-mabelita-primary transition-all shadow-lg">
                                    <Play size={18} className="ml-1" fill="currentColor" />
                                </div>
                            )}
                            <h4 className="text-white font-bold text-sm leading-tight mb-1 line-clamp-2">{item.title}</h4>
                            <div className="flex items-center justify-between text-[10px] text-gray-400 font-mono font-bold uppercase">
                                <span className="flex items-center gap-1"><Eye size={10} /> {item.views >= 1000000 ? (item.views / 1000000).toFixed(1) + 'M' : (item.views / 1000).toFixed(1) + 'K'}</span>
                                <span className={`${item.type === 'tiktok' ? 'text-[#00f2fe]' : item.type === 'youtube' ? 'text-red-400' : 'text-green-400'}`}>{item.filter}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
